import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  MapPin,
  Calendar,
  Video,
  ClipboardList,
  ListChecks,
  MessagesSquare,
  Notebook,
  Watch,
  Users,
  FileText,
  BookHeart,
  Mic,
  ArrowRight,
  ArrowLeft,
  Coins,
  Lock,
  Clock,
  Stethoscope,
  ExternalLink,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import {
  getStudyCountries,
  selectCompensation,
} from "../lib/countries";
import { formatClosingDate } from "../lib/dates";
import CompensationStack from "./CompensationStack";
import ShareInterestPopover from "./ShareInterestPopover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// Methodology icon mapping
// ============================================================
const formatMeta = (raw) => {
  const f = (raw || "Interview").toLowerCase();
  if (f.includes("survey")) return { Icon: ClipboardList, label: raw };
  if (f.includes("group")) return { Icon: Users, label: raw };
  if (f.includes("bulletin") || f.includes("discussion board"))
    return { Icon: MessagesSquare, label: raw };
  if (f.includes("diary") || f.includes("check-in"))
    return { Icon: Notebook, label: raw };
  if (f.includes("material") || f.includes("feedback"))
    return { Icon: FileText, label: raw };
  if (f.includes("journey")) return { Icon: BookHeart, label: raw };
  if (f.includes("wearable") || f.includes("app"))
    return { Icon: Watch, label: raw };
  if (f.includes("interview")) return { Icon: Mic, label: raw };
  return { Icon: Video, label: raw || "Interview" };
};

// ============================================================
// Diagnosis confirmation copy
// ============================================================
const DIAGNOSIS_COPY = {
  self_reported: {
    label: "Screener only",
    short: "Complete a screener, review the ICF, take part.",
    long: "You'll complete a screener so the research team can check the study applies to you. If it does, you'll review the Informed Consent Form and proceed with the research.",
  },
  self_plus_docs: {
    label: "Screener + documents",
    short: "Complete a screener and share documents that confirm your diagnosis or treatment.",
    long: "You'll complete a screener so the research team can check the study applies to you. You'll also be asked to provide documents that substantiate your diagnosis and/or treatment. If you're eligible, you'll review the Informed Consent Form and proceed with the research.",
  },
  physician_crf: {
    label: "Screener + physician confirmation",
    short: "Complete a screener and ask your physician to substantiate your diagnosis or treatment.",
    long: "You'll complete a screener so the research team can check the study applies to you. You'll also be asked to have your physician complete a short form substantiating your diagnosis and/or treatment. If you're eligible, you'll review the Informed Consent Form and proceed with the research.",
  },
};

// ============================================================
// StudyDetailsModal — three-step flow:
//   1) View details                (no friction)
//   2) Click CTA                   (opens screener if any, then form)
//   3) Submit Express-of-Interest  (email, phone, location, time)
// ============================================================
export const StudyDetailsModal = ({ study, audience = "patient", onClose }) => {
  const [step, setStep] = useState("details"); // "details" | "form" | "success"
  const [screenerOpened, setScreenerOpened] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!study) return null;
  const hasScreener = !!study.screener_url;
  const { Icon: FormatIcon, label: formatLabel } = formatMeta(study.format);
  const diagnosis = DIAGNOSIS_COPY[study.diagnosis_confirmation];

  const handlePrimaryCta = () => {
    if (hasScreener && !screenerOpened) {
      window.open(study.screener_url, "_blank", "noopener,noreferrer");
      setScreenerOpened(true);
      // Stay in details view; the contact form is now revealed too.
      setStep("form");
    } else {
      setStep("form");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="study-detail-title"
      data-testid="study-detail-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header , condition pill on the left, ref number on the
            right (replacing the redundant methodology pill, since the
            research-type pills already render lower in the body). */}
        <div className="sticky top-0 z-10 bg-white border-b border-[var(--light-border)] flex items-start justify-between gap-4 p-5 md:p-6">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <span className="badge">{study.condition}</span>
            {study.project_number && (
              <span
                className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[var(--text-muted)]"
                title={`Reference: ${study.project_number}`}
                data-testid="study-modal-header-ref"
              >
                Ref · {study.project_number}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -m-2 rounded-lg hover:bg-[var(--bone-soft)] transition-colors flex-shrink-0"
            aria-label="Close"
            data-testid="close-study-detail"
          >
            <X size={20} className="text-[var(--charcoal)]" />
          </button>
        </div>

        {/* Body — switches based on step */}
        {step === "success" ? (
          <SuccessView onClose={onClose} hasScreener={hasScreener} />
        ) : (
          <>
            <DetailsBody
              study={study}
              formatLabel={formatLabel}
              FormatIcon={FormatIcon}
              diagnosis={diagnosis}
            />

            {step === "form" && (
              <ExpressInterestForm
                study={study}
                audience={audience}
                screenerOpened={screenerOpened}
                hasScreener={hasScreener}
                onSubmitted={() => setStep("success")}
                onBack={() => setStep("details")}
              />
            )}

            {step === "details" && (
              <FooterCta
                study={study}
                audience={audience}
                hasScreener={hasScreener}
                onPrimary={handlePrimaryCta}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Sub-views
// ============================================================
function DetailsBody({ study, diagnosis }) {
  const countries = getStudyCountries(study);
  const { rows: compRows, fallback: compFallback } = selectCompensation(
    study,
    null,
  );
  const types =
    Array.isArray(study.research_types) && study.research_types.length > 0
      ? study.research_types
      : study.format
        ? [study.format]
        : [];

  return (
    <div className="p-5 md:p-7 space-y-6">
      <h2
        id="study-detail-title"
        className="font-['Outfit'] font-bold text-[var(--charcoal)] text-2xl md:text-3xl leading-tight"
      >
        {study.title}
      </h2>

      {/* Research-type pills , one per selected methodology. Same look
          as the StudyCard so the visual identity stays consistent. */}
      {types.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {types.map((t) => {
            const Icon = pickMethodIcon(t);
            return (
              <span
                key={t}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[var(--light-card)] text-[var(--navy)] border border-[var(--light-border)]"
              >
                <Icon size={13} />
                {t}
              </span>
            );
          })}
        </div>
      )}

      {/* Synopsis , surfaced high up (just below methodology pills)
          so visitors see the study purpose before the facts/comp. */}
      <div>
        <h3 className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-2">
          Synopsis
        </h3>
        <p className="text-[var(--charcoal)] leading-relaxed whitespace-pre-line">
          {study.description}
        </p>
      </div>

      {/* Top facts grid , just Time + Closes now. Confirmation lives
          in its own dedicated block lower down. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Fact icon={Clock} label="Time" value={study.time_commitment || "Varies"} />
        <Fact icon={Calendar} label="Closes" value={formatClosingDate(study.end_date)} />
      </div>

      {/* Eligibility process block */}
      {diagnosis && (
        <div className="rounded-xl border border-[var(--light-border)] bg-[var(--bone-soft)] p-4">
          <div className="flex items-start gap-3">
            <Stethoscope size={18} className="text-[var(--teal)] mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-1">
                Eligibility process
              </p>
              <p className="text-sm font-semibold text-[var(--charcoal)] mb-1">
                {diagnosis.label}
              </p>
              <p className="text-sm text-[var(--text-muted)] leading-relaxed">
                {diagnosis.long || diagnosis.short}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Privacy reassurance */}
      <div className="flex items-start gap-3 rounded-xl bg-white border border-[var(--light-border)] p-4">
        <Lock size={18} className="text-[var(--teal)] mt-0.5 flex-shrink-0" />
        <p className="text-sm text-[var(--text-muted)] leading-relaxed">
          Your details stay private and anonymized in research outputs. We
          only contact you about studies that match, and you can withdraw
          at any time.
        </p>
      </div>

      {/* Thank-you / compensation panel , moved to the end of the
          modal body so the user reads the study details first. Rows
          are forced single-column (`grid-cols-1`) so they stack one
          above the other regardless of viewport, matching the home
          page card style. */}
      <div
        className="rounded-xl border border-[var(--lime)]/40 bg-[var(--lime)]/[0.08] p-4"
        data-testid="study-multi-country-block"
      >
        <p className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-3 flex items-center gap-1.5">
          <Coins size={13} />
          Thank-you
          {countries.length >= 2 && (
            <span className="ml-1 text-[var(--olive)] font-medium normal-case tracking-normal">
              , {countries.length} countries
            </span>
          )}
        </p>
        <CompensationStack
          rows={compRows}
          fallback={compFallback}
          variant="panel-stacked"
          testId="study-compensation-stack"
        />
      </div>
    </div>
  );
}

// Pick a Lucide icon for a research-type label. Used by the modal +
// detail page pills.
function pickMethodIcon(raw) {
  const f = (raw || "").toLowerCase();
  if (f.includes("survey")) return ClipboardList;
  if (f.includes("bulletin") || f.includes("discussion board"))
    return MessagesSquare;
  if (f.includes("group")) return Users;
  if (f.includes("pre-task") || f.includes("post-task") || f.includes("pretask") || f.includes("posttask"))
    return ListChecks;
  if (f.includes("diary") || f.includes("check-in")) return Notebook;
  if (f.includes("material") || f.includes("feedback")) return FileText;
  if (f.includes("journey")) return BookHeart;
  if (f.includes("wearable") || f.includes("app")) return Watch;
  if (f.includes("interview")) return Mic;
  return Video;
}

function Fact({ icon: Icon, label, value, highlight = false }) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        highlight
          ? "border-[var(--lime)] bg-[var(--lime)]/12"
          : "border-[var(--light-border)] bg-white"
      }`}
    >
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-1">
        <Icon size={13} />
        {label}
      </div>
      <div className="text-[var(--charcoal)] font-semibold text-sm leading-tight">
        {value}
      </div>
    </div>
  );
}

function FooterCta({ study, audience, hasScreener, onPrimary }) {
  return (
    <div className="sticky bottom-0 bg-white border-t border-[var(--light-border)] p-5 md:p-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
      {/* Secondary: share with someone who might benefit. Sits left
          of the primary CTA so it doesn't compete visually but is
          still one tap away , especially valuable for patients who
          aren't a fit themselves but know someone who is. */}
      <ShareInterestPopover study={study} audience={audience} />

      <button
        type="button"
        onClick={onPrimary}
        className="btn-teal inline-flex items-center justify-center gap-2"
        data-testid="study-detail-cta"
      >
        {hasScreener ? (
          <>
            Take 2-min screener
            <ExternalLink size={16} />
          </>
        ) : (
          <>
            Register your interest
            <ArrowRight size={18} />
          </>
        )}
      </button>
    </div>
  );
}

// ============================================================
// Express Interest form
// ============================================================
const TIME_INPUT_CLS =
  "w-full h-11 px-4 rounded-lg border border-[var(--light-border)] bg-white text-[var(--charcoal)] focus:outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20";

function ExpressInterestForm({
  study,
  audience,
  hasScreener,
  screenerOpened,
  onSubmitted,
  onBack,
}) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_prefix: "",
    phone_number: "",
    interest_country: "",
    location: "",
    preferred_call_at: "",
    notes: "",
    screener_completed: hasScreener ? false : true,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const studyCountries = getStudyCountries(study);
  const isMultiCountryStudy = studyCountries.length >= 2;
  // Pre-fill the country picker on multi-country studies if the study
  // only has one country (single-country fast path , obvious answer).
  useEffect(() => {
    if (studyCountries.length === 1 && !form.interest_country) {
      setForm((f) => ({ ...f, interest_country: studyCountries[0].name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study?.id]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Required";
    if (!form.last_name.trim()) e.last_name = "Required";
    if (!form.email.trim() || !/.+@.+\..+/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.phone_prefix.trim()) e.phone_prefix = "Required";
    if (
      !form.phone_number.trim() ||
      form.phone_number.replace(/\D/g, "").length < 6
    )
      e.phone_number = "Enter a valid mobile number";
    if (isMultiCountryStudy && !form.interest_country)
      e.interest_country = "Please pick which country you're applying from";
    if (!form.location.trim()) e.location = "City + time zone, please";
    if (!form.preferred_call_at) e.preferred_call_at = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      // Server still reads `location` (used for "City + time zone"). We
      // also pass `interest_country` on multi-country studies so the
      // admin pipeline knows which country to route the lead to. For
      // single-country studies, `interest_country` is auto-filled to
      // match.
      const payload = {
        ...form,
        study_id: study.id,
        audience,
        source: hasScreener ? "screener" : "direct",
      };
      await axios.post(`${API}/studies/${study.id}/interest`, payload);
      onSubmitted?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Min datetime = now (so they can't pick the past)
  const nowIso = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);

  return (
    <form
      onSubmit={submit}
      className="border-t border-[var(--light-border)] bg-[var(--bone-soft)] p-5 md:p-7 space-y-5"
      data-testid="express-interest-form"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-xl">
          {hasScreener
            ? "Once you've completed the screener, leave your details"
            : "Leave your contact details"}
        </h3>
      </div>

      {hasScreener && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-[var(--charcoal)] flex items-start gap-2">
          <ExternalLink size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
          <span>
            {screenerOpened
              ? "We've opened the screener in a new tab. Once it confirms you're eligible, finish below and we'll be in touch."
              : "Please take the screener in the new tab we opened. If it confirms you're eligible, fill in the form below."}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="First name" error={errors.first_name}>
          <input
            data-testid="interest-first-name"
            value={form.first_name}
            onChange={(e) => setField("first_name", e.target.value)}
            className={TIME_INPUT_CLS}
            autoComplete="given-name"
          />
        </Field>
        <Field label="Last name" error={errors.last_name}>
          <input
            data-testid="interest-last-name"
            value={form.last_name}
            onChange={(e) => setField("last_name", e.target.value)}
            className={TIME_INPUT_CLS}
            autoComplete="family-name"
          />
        </Field>
        <Field label="Email" error={errors.email} className="sm:col-span-2">
          <input
            data-testid="interest-email"
            type="email"
            inputMode="email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            className={TIME_INPUT_CLS}
            autoComplete="email"
          />
        </Field>
        <Field label="Country code" error={errors.phone_prefix}>
          <input
            data-testid="interest-phone-prefix"
            value={form.phone_prefix}
            onChange={(e) => setField("phone_prefix", e.target.value)}
            placeholder="+1"
            className={TIME_INPUT_CLS}
            inputMode="tel"
          />
        </Field>
        <Field label="Mobile number" error={errors.phone_number}>
          <input
            data-testid="interest-phone-number"
            value={form.phone_number}
            onChange={(e) => setField("phone_number", e.target.value)}
            className={TIME_INPUT_CLS}
            placeholder="6187805565"
            autoComplete="tel-national"
            inputMode="tel"
          />
        </Field>
        {isMultiCountryStudy && (
          <Field
            label="Which country are you applying from?"
            error={errors.interest_country}
            helper="This study is open in more than one country , let us know which one applies to you."
            className="sm:col-span-2"
          >
            <select
              data-testid="interest-country"
              value={form.interest_country}
              onChange={(e) => setField("interest_country", e.target.value)}
              className={TIME_INPUT_CLS}
            >
              <option value="">Select country…</option>
              {studyCountries.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field
          label="City + time zone"
          error={errors.location}
          helper="So we know what time we're calling"
          className="sm:col-span-2"
        >
          <input
            data-testid="interest-location"
            value={form.location}
            onChange={(e) => setField("location", e.target.value)}
            placeholder="e.g. London, UK (GMT) or Boston, MA (EST)"
            className={TIME_INPUT_CLS}
          />
        </Field>
        <Field
          label="Best time for us to call"
          error={errors.preferred_call_at}
          helper="In your local time"
          className="sm:col-span-2"
        >
          <input
            data-testid="interest-preferred-time"
            type="datetime-local"
            min={nowIso}
            value={form.preferred_call_at}
            onChange={(e) => setField("preferred_call_at", e.target.value)}
            className={TIME_INPUT_CLS}
          />
        </Field>
        <Field label="Anything else? (optional)" className="sm:col-span-2">
          <textarea
            data-testid="interest-notes"
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
            rows={2}
            className="w-full px-4 py-2 rounded-lg border border-[var(--light-border)] bg-white text-[var(--charcoal)] focus:outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20"
          />
        </Field>
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-[var(--text-muted)] hover:text-[var(--charcoal)] inline-flex items-center gap-1.5"
          data-testid="interest-back"
        >
          <ArrowLeft size={14} />
          Back to study details
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="btn-teal inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="interest-submit"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              Submit
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </div>
    </form>
  );
}

function Field({ label, error, helper, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-medium text-[var(--charcoal)]">
          {label}
        </span>
        {error && <span className="text-xs text-red-600">{error}</span>}
      </div>
      {children}
      {helper && !error && (
        <span className="text-xs text-[var(--text-muted)] mt-1 block">
          {helper}
        </span>
      )}
    </label>
  );
}

function SuccessView({ onClose }) {
  return (
    <div className="p-8 md:p-10 text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[var(--lime)]/20 mb-5">
        <CheckCircle2 size={32} className="text-[var(--teal)]" />
      </div>
      <h3 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-2xl mb-2">
        Thanks, we'll be in touch.
      </h3>
      <p className="text-[var(--text-muted)] max-w-md mx-auto mb-6">
        Someone from our team will call you at the time you suggested. If you
        need to change anything, just reply to the confirmation email.
      </p>
      <button
        type="button"
        onClick={onClose}
        className="btn-secondary-light"
        data-testid="success-close"
      >
        Close
      </button>
    </div>
  );
}

export default StudyDetailsModal;
